import { Dialog, Transition } from "@headlessui/react";
import { Fragment, type HTMLAttributes, useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { SettlementApi } from "../../api";
import { useTerminalStore } from "../../store";
import { Spinner } from "../atoms";
import { CopyButton } from "./CopyButton";
import { ConfirmDialog } from "./ConfirmDialog";
import { useProfile } from "../../hooks";
import { TwoFactorAuthDialog } from "./TwoFactorAuthDialog";
import type { AxiosError } from "axios";

const POLLING_INTERVAL = 2000;
const CHECK_DELAY = 1000;

const formatNumberInput = (value: string): string => {
  const cleaned = value.replace(/[^\d.,]/g, '');
  if (!cleaned) return '';
  
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  
  let normalized = cleaned;
  if (hasComma && hasDot) {
    const commaIndex = cleaned.indexOf(',');
    const dotIndex = cleaned.indexOf('.');
    if (commaIndex < dotIndex) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(/\./g, '');
      normalized = normalized.replace(',', '.');
    }
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  }
  
  const parts = normalized.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  if (parts.length === 2 && parts[1].length > 2) {
    return parts[0] + '.' + parts[1].slice(0, 2);
  }
  return normalized;
};

const parseNumber = (value: string): number => {
  const normalized = value.replace(',', '.');
  return parseFloat(normalized) || 0;
};

const formatNumberDisplay = (value: number | ""): string => {
  if (value === "" || value === 0) return '';
  return value.toFixed(2);
};

export const SettlementDialog = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
} & HTMLAttributes<HTMLDivElement>) => {
  const { terminalId } = useTerminalStore();
  const profile = useProfile();

  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState<number | "">("");
  const [amountInput, setAmountInput] = useState("");
  const [usdtAmount, setUsdtAmount] = useState<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ address?: string; amount?: string }>({});
  const [touchedFields, setTouchedFields] = useState<{ address: boolean; amount: boolean }>({ address: false, amount: false });
  const [isCreating, setIsCreating] = useState(false);
  const [settlementId, setSettlementId] = useState<string | null>(null);
  const [status, setStatus] = useState<'PENDING' | 'DONE' | 'CANCELED' | null>(null);
  const [txId, setTxId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [is2FADialogOpen, setIs2FADialogOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasUserInput, setHasUserInput] = useState(false);

  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAmountInputRef = useRef<number>(0);
  /** Чтобы попап Success показывался один раз на каждый переход в DONE */
  const prevStatusFromApiRef = useRef<string | undefined>(undefined);
  /** Адрес прошёл check API — можно вводить amount */
  const [addressApiValid, setAddressApiValid] = useState(false);
  const addressValidateDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const addressValidationRequestIdRef = useRef(0);
  const initialAddressValidatedRef = useRef(false);

  const { data: settlementInfo, isLoading: isLoadingInfo } = useQuery({
    enabled: isOpen && !!terminalId,
    queryKey: [SettlementApi.KEY, 'info', terminalId],
    queryFn: () => SettlementApi.getSettlementInfo(terminalId!),
  });

  // Инициализация формы только при первом открытии
  useEffect(() => {
    if (settlementInfo && isOpen && !isInitialized) {
      // Инициализируем поля только если пользователь не вводил данные
      if (settlementInfo.address !== undefined && !hasUserInput) {
        setAddress(settlementInfo.address || '');
      }
      if (settlementInfo.amount !== undefined && !hasUserInput) {
        const amountValue = settlementInfo.amount || '';
        setAmount(amountValue);
        setAmountInput(amountValue === '' ? '' : formatNumberDisplay(amountValue as number));
      }
      if (settlementInfo.settlementAmount !== null && settlementInfo.settlementAmount !== undefined) {
        setUsdtAmount(settlementInfo.settlementAmount);
      } else {
        setUsdtAmount(null);
      }
      
      // Восстанавливаем состояние активного settlement
      if (settlementInfo.hasActiveSettlement && settlementInfo.settlementId) {
        setSettlementId(settlementInfo.settlementId);
        setStatus('PENDING');
        setIsCreating(true);
      } else {
        setSettlementId(null);
        setStatus(null);
        setIsCreating(false);
      }
      
      setIsInitialized(true);
    }
  }, [settlementInfo, isOpen, isInitialized, hasUserInput]);

  useEffect(() => {
    prevStatusFromApiRef.current = undefined;
  }, [settlementId]);

  const validateAddressMutation = useMutation({
    mutationFn: SettlementApi.checkSettlement,
  });

  const runAddressValidation = (addressValue: string) => {
    if (!terminalId || !settlementInfo || !addressValue.trim()) {
      return;
    }
    addressValidationRequestIdRef.current += 1;
    const requestId = addressValidationRequestIdRef.current;
    const trimmed = addressValue.trim();
    const min = settlementInfo.limits.min;
    validateAddressMutation.mutate(
      { terminalId, address: trimmed, amount: min },
      {
        onSuccess: () => {
          if (requestId !== addressValidationRequestIdRef.current) {
            return;
          }
          setAddressApiValid(true);
          setFieldErrors((prev) => ({ ...prev, address: undefined }));
        },
        onError: (error: any) => {
          if (requestId !== addressValidationRequestIdRef.current) {
            return;
          }
          setAddressApiValid(false);
          if (error.response?.status === 403 && (error.response?.data as any)?.title === '2FA ENABLE NEEDS') {
            setIs2FADialogOpen(true);
            return;
          }
          if (SettlementApi.isCheckError(error)) {
            const fieldName = error.response?.data?.field?.name;
            const fieldDescription = error.response?.data?.field?.description;
            if (fieldName === 'address') {
              setFieldErrors((prev) => ({ ...prev, address: fieldDescription }));
            } else if (fieldName === 'amount') {
              setFieldErrors((prev) => ({ ...prev, address: undefined }));
              setAddressApiValid(true);
            }
          }
        },
      }
    );
  };

  const checkMutation = useMutation({
    mutationFn: SettlementApi.checkSettlement,
    onSuccess: (data) => {
      setUsdtAmount(data.amount);
      setFieldErrors({});
    },
    onError: (error: any) => {
      if (error.response?.status === 403 && (error.response?.data as any)?.title === '2FA ENABLE NEEDS') {
        setIs2FADialogOpen(true);
        setUsdtAmount(null);
        return;
      }
      if (SettlementApi.isCheckError(error)) {
        const fieldName = error.response?.data?.field?.name;
        const fieldDescription = error.response?.data?.field?.description;

        if (fieldName === 'amount') {
          setFieldErrors(prev => ({ ...prev, amount: fieldDescription }));
        } else if (fieldName === 'address') {
          setFieldErrors(prev => ({ ...prev, address: fieldDescription }));
          setAddressApiValid(false);
        }
        setUsdtAmount(null);
      }
    }
  });

  const createMutation = useMutation({
    mutationFn: SettlementApi.createSettlement,
    onSuccess: (data) => {
      setSettlementId(data.settlementId);
      setStatus('PENDING');
      setIsCreating(true);
    },
    onError: (error: any) => {
      if (error.response?.status === 403 && (error.response?.data as any)?.title === '2FA ENABLE NEEDS') {
        setIs2FADialogOpen(true);
        setIsCreating(false);
        return;
      }
      if (SettlementApi.isCheckError(error)) {
        const fieldName = error.response?.data?.field?.name;
        const fieldDescription = error.response?.data?.field?.description;

        if (fieldName === 'amount') {
          setFieldErrors(prev => ({ ...prev, amount: fieldDescription }));
        } else if (fieldName === 'address') {
          setFieldErrors(prev => ({ ...prev, address: fieldDescription }));
        }
      }
      setIsCreating(false);
    }
  });

  const statusQuery = useQuery({
    enabled: !!settlementId && !!terminalId && isOpen && (settlementInfo?.hasActiveSettlement || status === 'PENDING' || isCreating),
    queryKey: [SettlementApi.KEY, 'status', terminalId, settlementId],
    queryFn: () => SettlementApi.getSettlementStatus(terminalId!, settlementId!),
    refetchInterval: status === 'DONE' || status === 'CANCELED' ? false : POLLING_INTERVAL,
  });

  useEffect(() => {
    if (statusQuery.data) {
      const data = statusQuery.data;
      if (data.txId) {
        setTxId(data.txId);
      }
      if (data.status === 'DONE') {
        setStatus('DONE');
        setIsCreating(false);
        if (prevStatusFromApiRef.current !== 'DONE') {
          setShowSuccessDialog(true);
        }
        prevStatusFromApiRef.current = 'DONE';
      } else if (data.status === 'CANCELED') {
        setStatus('CANCELED');
        setIsCreating(false);
        setCancelError(data.description || 'Settlement was canceled');
        if (!showCancelDialog) {
          setShowCancelDialog(true);
        }
        prevStatusFromApiRef.current = 'CANCELED';
      } else if (data.status === 'PENDING') {
        setStatus('PENDING');
        prevStatusFromApiRef.current = 'PENDING';
      }
    }
  }, [statusQuery.data, showCancelDialog]);

  /** Prefilled address с бэка: один раз проверить через API */
  useEffect(() => {
    if (!isOpen || !isInitialized) {
      return;
    }
    if (settlementInfo?.hasActiveSettlement || hasUserInput) {
      return;
    }
    if (!address.trim() || initialAddressValidatedRef.current) {
      return;
    }
    initialAddressValidatedRef.current = true;
    const t = setTimeout(() => runAddressValidation(address), 100);
    return () => clearTimeout(t);
  }, [isInitialized, isOpen, hasUserInput, settlementInfo?.hasActiveSettlement, address]);

  const validateAmount = (value: number | "") => {
    if (value === "" || value === 0) {
      return false;
    }
    if (settlementInfo) {
      if (value < settlementInfo.limits.min) {
        setFieldErrors(prev => ({ ...prev, amount: `Amount must be at least ${settlementInfo.limits.min}` }));
        return false;
      }
      if (value > settlementInfo.limits.max) {
        setFieldErrors(prev => ({ ...prev, amount: `Amount must be at most ${settlementInfo.limits.max}` }));
        return false;
      }
    }
    return true;
  };

  const isFormDisabled =
    settlementInfo?.hasActiveSettlement || status === 'PENDING' || isCreating || createMutation.isPending;

  const handleAddressChange = (value: string) => {
    if (addressValidateDebounceRef.current) {
      clearTimeout(addressValidateDebounceRef.current);
      addressValidateDebounceRef.current = null;
    }
    setAddress(value);
    setAddressApiValid(false);
    setFieldErrors((prev) => ({ ...prev, address: undefined, amount: undefined }));
    setUsdtAmount(null);
    setAmount('');
    setAmountInput('');
    setHasUserInput(true);

    if (touchedFields.address && !value.trim()) {
      setFieldErrors((prev) => ({ ...prev, address: 'Field is required' }));
    }

    if (value.trim() && !isFormDisabled) {
      addressValidateDebounceRef.current = setTimeout(() => {
        runAddressValidation(value);
      }, 600);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!addressApiValid) {
      return;
    }
    const formatted = formatNumberInput(e.target.value);
    const numValue = formatted === '' ? '' : parseNumber(formatted);
    
    setAmountInput(formatted);
    setAmount(numValue);
    setHasUserInput(true);
    setFieldErrors(prev => ({ ...prev, amount: undefined }));

    if (touchedFields.amount && (numValue === "" || numValue === 0)) {
      setFieldErrors(prev => ({ ...prev, amount: 'Field is required' }));
    }

    if (usdtAmount !== null) {
      setUsdtAmount(null);
    }

    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    if (numValue !== "" && numValue !== 0) {
      if (!validateAmount(numValue as number)) {
        return;
      }
    }

    lastAmountInputRef.current = Date.now();
    checkTimeoutRef.current = setTimeout(() => {
      const addr = address.trim();
      if (
        addr &&
        addressApiValid &&
        numValue !== '' &&
        numValue !== 0 &&
        validateAmount(numValue as number)
      ) {
        checkMutation.mutate({
          terminalId: terminalId!,
          address: addr,
          amount: parseFloat((numValue as number).toFixed(2)),
        });
      }
    }, CHECK_DELAY);
  };

  const handleAddressBlur = () => {
    setTouchedFields((prev) => ({ ...prev, address: true }));
    if (addressValidateDebounceRef.current) {
      clearTimeout(addressValidateDebounceRef.current);
      addressValidateDebounceRef.current = null;
    }
    if (!address.trim()) {
      setFieldErrors((prev) => ({ ...prev, address: 'Field is required' }));
      setAddressApiValid(false);
      return;
    }
    runAddressValidation(address);
  };

  const handleAmountBlur = () => {
    setTouchedFields(prev => ({ ...prev, amount: true }));
    if (amount === "" || amount === 0) {
      setFieldErrors(prev => ({ ...prev, amount: 'Field is required' }));
      setAmountInput('');
    } else {
      validateAmount(amount as number);
      setAmountInput(formatNumberDisplay(amount as number));
    }
  };

  const handleDone = () => {
    if (!profile?.is2faEnabled) {
      setIs2FADialogOpen(true);
      return;
    }

    const addr = address.trim();
    if (!addr || !amount || amount === 0) {
      return;
    }
    if (usdtAmount === null) {
      return;
    }
    createMutation.mutate({
      terminalId: terminalId!,
      address: addr,
      amount: parseFloat((amount as number).toFixed(2)),
    });
  };

  const amountInputEnabled = addressApiValid && !isFormDisabled && !validateAddressMutation.isPending;
  const canSubmit =
    addressApiValid &&
    !!address.trim() &&
    !!amount &&
    amount !== 0 &&
    usdtAmount !== null &&
    !isFormDisabled &&
    !checkMutation.isPending &&
    !validateAddressMutation.isPending;
  const isPendingStatus = status === 'PENDING' || isCreating || createMutation.isPending || statusQuery.isFetching;
  const isDoneStatus = status === 'DONE';
  const isCanceledStatus = status === 'CANCELED';

  /** После успешной проверки адреса подтянуть USDT, если сумма уже была (prefill) */
  useEffect(() => {
    const formLocked =
      settlementInfo?.hasActiveSettlement || status === 'PENDING' || isCreating || createMutation.isPending;
    if (!addressApiValid || formLocked || !terminalId || !settlementInfo) {
      return;
    }
    if (amount === '' || amount === 0) {
      return;
    }
    const n = amount as number;
    if (n < settlementInfo.limits.min || n > settlementInfo.limits.max) {
      return;
    }
    const addr = address.trim();
    if (!addr) {
      return;
    }
    const t = setTimeout(() => {
      checkMutation.mutate({
        terminalId,
        address: addr,
        amount: parseFloat(n.toFixed(2)),
      });
    }, CHECK_DELAY);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при смене addressApiValid
  }, [addressApiValid]);

  useEffect(() => {
    if (!isOpen) {
      // Всегда сбрасываем флаги при закрытии формы
      setIsInitialized(false);
      setHasUserInput(false);
      
      if (!settlementInfo?.hasActiveSettlement) {
        setAddress("");
        setAmount("");
        setAmountInput("");
        setUsdtAmount(null);
        setFieldErrors({});
        setTouchedFields({ address: false, amount: false });
        setIsCreating(false);
        setSettlementId(null);
        setStatus(null);
        setTxId(null);
        setCancelError(null);
        setShowCancelDialog(false);
        setShowSuccessDialog(false);
        setAddressApiValid(false);
        initialAddressValidatedRef.current = false;
      }
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
      if (addressValidateDebounceRef.current) {
        clearTimeout(addressValidateDebounceRef.current);
        addressValidateDebounceRef.current = null;
      }
    }
  }, [isOpen, settlementInfo?.hasActiveSettlement]);

  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, []);

  if (!settlementInfo && isLoadingInfo) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Spinner />
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  if (!settlementInfo) {
    return null;
  }

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <div className="bg-white px-8 py-10 rounded-3xl w-[768px] relative">
                  {checkMutation.isPending && (
                    <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-3xl z-10">
                      <Spinner />
                    </div>
                  )}

                  <div className="grid gap-6">
                    <div className="grid gap-4 pb-4 border-b border-dashed border-gray-300">
                      <div className="grid grid-cols-[200px_1fr] items-center">
                        <span className="text-brand-2">Currency:</span>
                        <div className="bg-gray-100 px-3 py-2 rounded text-brand-6">
                          {settlementInfo.currency.code}
                        </div>
                      </div>
                      <div className="grid grid-cols-[200px_1fr] items-center">
                        <span className="text-brand-2">Amount available for settlement:</span>
                        <div className="flex gap-2 items-center">
                          <div className="bg-gray-100 px-3 py-2 rounded text-brand-6">
                            {settlementInfo.limits.min.toLocaleString()}
                          </div>
                          <span>-</span>
                          <div className="bg-gray-100 px-3 py-2 rounded text-brand-6">
                            {settlementInfo.limits.max.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-[200px_1fr] items-center">
                        <span className="text-brand-2">{settlementInfo.currency.code}/USDT rate:</span>
                        <div className="bg-gray-100 px-3 py-2 rounded text-brand-6">
                          {settlementInfo.rate}
                        </div>
                      </div>
                      <div className="grid grid-cols-[200px_1fr] items-center">
                        <span className="text-brand-2">Settlement fee (%):</span>
                        <div className="bg-gray-100 px-3 py-2 rounded text-brand-6">
                          {settlementInfo.fee}
                        </div>
                      </div>
                    </div>

                    <div className="font-bold text-brand-6">
                      Complete the information below in order to make settlement payment
                    </div>

                    <div className="grid gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm text-brand-2 mb-2">
                          <span>USDT TRON wallet address:</span>
                          {validateAddressMutation.isPending && !isFormDisabled && (
                            <span className="inline-flex h-4 w-4 border-2 border-brand-6 border-e-transparent rounded-full animate-spin" />
                          )}
                        </label>
                        <input
                          className={`input w-full ${fieldErrors.address ? 'border-red-500 border-2' : ''}`}
                          value={address}
                          onChange={(e) => handleAddressChange(e.target.value)}
                          onBlur={handleAddressBlur}
                          disabled={isFormDisabled}
                          type="text"
                          placeholder="Enter wallet address"
                        />
                        {fieldErrors.address && (
                          <span className="block mt-1 text-sm text-red-600">{fieldErrors.address}</span>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm text-brand-2 mb-2">Amount (in local currency):</label>
                        <input
                          className={`input w-full ${!amountInputEnabled && !isFormDisabled ? 'bg-gray-100 text-brand-2' : ''} ${fieldErrors.amount ? 'border-red-500 border-2' : ''}`}
                          value={amountInput}
                          onChange={handleAmountChange}
                          onBlur={handleAmountBlur}
                          disabled={isFormDisabled || !amountInputEnabled}
                          type="text"
                          placeholder={
                            isFormDisabled
                              ? 'Enter amount'
                              : addressApiValid
                                ? 'Enter amount'
                                : 'Enter a valid wallet address first'
                          }
                        />
                        {fieldErrors.amount && (
                          <span className="block mt-1 text-sm text-red-600">{fieldErrors.amount}</span>
                        )}
                      </div>

                      {usdtAmount !== null && (
                        <div>
                          <label className="block text-sm text-brand-2 mb-2">Amount ready for transfer (USDT):</label>
                          <div className="bg-gray-100 px-3 py-2 rounded text-brand-6">
                            {usdtAmount.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      )}

                      {txId && (
                        <div>
                          <label className="block text-sm text-brand-2 mb-2">Transaction ID:</label>
                          <div className="flex items-center gap-2">
                            <div className="bg-gray-100 px-3 py-2 rounded text-brand-6 flex-1">
                              {txId}
                            </div>
                            <CopyButton value={txId} />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-4 mt-4">
                      {isPendingStatus || createMutation.isPending ? (
                        <button
                          className="flex-1 button button--block button--primary h-12 flex items-center justify-center"
                          disabled
                        >
                          <div className="h-4 w-4 border-2 border-white border-e-transparent rounded-full animate-spin" />
                        </button>
                      ) : isDoneStatus ? (
                        <button
                          type="button"
                          className="flex-1 h-12 flex items-center justify-center rounded-xl font-bold text-white shadow-lg ring-2 ring-lime-300 bg-gradient-to-b from-lime-400 to-green-600 cursor-default opacity-100"
                          disabled
                          aria-disabled
                        >
                          SUCCESS
                        </button>
                      ) : isCanceledStatus ? (
                        <button
                          className="flex-1 button button--block bg-red-600 text-white h-12 flex items-center justify-center"
                          disabled
                        >
                          CANCELED
                        </button>
                      ) : (
                        <button
                          className="flex-1 button button--block button--primary h-12 flex items-center justify-center"
                          disabled={!canSubmit}
                          onClick={handleDone}
                        >
                          DONE
                        </button>
                      )}
                      <button
                        className="flex-1 button button--block bg-orange-500 text-white h-12 flex items-center justify-center"
                        onClick={onClose}
                      >
                        {isDoneStatus || isCanceledStatus ? 'CLOSE' : 'CANCEL'}
                      </button>
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <ConfirmDialog
        isOpen={showCancelDialog}
        title="Error"
        paragraph={cancelError || "Settlement was canceled"}
        onContinue={() => setShowCancelDialog(false)}
        onClick={() => setShowCancelDialog(false)}
      />

      <Transition appear show={showSuccessDialog} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[110]"
          onClose={() => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" aria-hidden />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white px-8 py-8 shadow-xl">
                  <p className="text-center text-xl font-semibold text-green-600 mb-6">
                    Success !
                  </p>
                  <button
                    type="button"
                    className="button button--block button--primary w-full h-12"
                    onClick={() => setShowSuccessDialog(false)}
                  >
                    OK
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <TwoFactorAuthDialog isOpen={is2FADialogOpen} onClose={() => setIs2FADialogOpen(false)} />
    </>
  );
};
